// UI-only worker health card for the admin dashboard. No data fetching.
//
// Status colours follow the catalog tokens (§6.1): success for healthy,
// warning for degraded/starting, muted for stopped, error for failed.
import type { ReactElement } from 'react';
import { Badge, Card, Typography } from 'antd';

import { useTranslation } from '@govard/i18n-core/react';

import { formatAdminCount, formatDateTime } from '../lib/format.js';
import type { AdminWorkerHealth } from '../api/admin.client.js';

const { Text } = Typography;

export interface WorkerHealthCardProps {
  /** Translated worker name. */
  title: string;
  health: AdminWorkerHealth;
  /** data-testid for the card root. */
  testId?: string;
}

function statusTone(status: string, running: boolean): 'success' | 'warning' | 'error' | 'default' {
  if (status === 'failed') return 'error';
  if (status === 'degraded' || status === 'starting') return 'warning';
  if (!running || status === 'stopped') return 'default';
  return 'success';
}

function statusKey(status: string, running: boolean): string {
  if (status === 'failed') return 'crm.admin.workers.status_failed';
  if (status === 'degraded') return 'crm.admin.workers.status_degraded';
  if (status === 'starting') return 'crm.admin.workers.status_starting';
  if (!running || status === 'stopped') return 'crm.admin.workers.status_stopped';
  return 'crm.admin.workers.status_healthy';
}

export function WorkerHealthCard({ title, health, testId }: WorkerHealthCardProps): ReactElement {
  const { t } = useTranslation();

  const tone = statusTone(health.status, health.running);

  return (
    <Card size="small" data-testid={testId} styles={{ body: { padding: 16 } }}>
      <div className="flex items-center justify-between gap-3">
        <Text className="text-[13px] font-semibold text-[var(--ant-color-text,#44403c)]">
          {title}
        </Text>
        <Badge
          status={tone}
          text={t(statusKey(health.status, health.running))}
          data-testid={`${testId ?? 'worker'}-status`}
        />
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-[var(--ant-color-text-secondary)]">
        <span>
          {t('crm.admin.workers.ticks')}: {formatAdminCount(health.ticksStarted ?? 0)}
        </span>
        <span>
          {t('crm.admin.workers.completed')}: {formatAdminCount(health.ticksCompleted ?? 0)}
        </span>
        <span className={tone === 'error' || tone === 'warning' ? 'font-semibold' : undefined}>
          {t('crm.admin.workers.errors')}: {formatAdminCount(health.tickErrors ?? 0)}
          {(health.consecutiveErrors ?? 0) > 0
            ? ` (${formatAdminCount(health.consecutiveErrors ?? 0)})`
            : ''}
        </span>
      </div>
      <Text className="mt-2 block text-xs" style={{ color: 'var(--ant-color-text-tertiary)' }}>
        {t('crm.admin.workers.last_tick')}: {formatDateTime(health.lastTickFinishedAt ?? null)}
      </Text>
    </Card>
  );
}
